import { useCallback, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { renameSectionInPage } from '../../services/PageOperations';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';

/** Presentation state for the "Edit section title" modal on the Contents tab. */
export interface EditSectionButtonViewModel {
  readonly isModalVisible: boolean;
  readonly title: TiptapOutputType;
  /** The optional navigation/table-of-contents short title. */
  readonly shortTitle: TiptapOutputType;
  /** True when the Save button should be enabled. */
  readonly isSaveEnabled: boolean;
  /** True while the new title is being persisted. */
  readonly isSaving: boolean;
  /** Inline error to display after a failed save attempt. Undefined when no error. */
  readonly saveErrorMessage: string | undefined;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Persist the new title and close the modal. No-op when invalid or already saving. */
  readonly confirmSave: () => void;
  readonly updateTitle: (newTitle: TiptapOutputType, isValidationPassed: boolean) => void;
  readonly updateShortTitle: (newShortTitle: TiptapOutputType, isValidationPassed: boolean) => void;
}

/**
 * Drives the "Edit section title" modal for a Guide page section. Saves the
 * new title via ContentEditorService.stageEdit + flush, regenerating the
 * section's href from the new title to match legacy behavior. Length bounds
 * are enforced by the input, not the view model.
 */
export function useEditSectionButtonViewModel(
  currentPage: ContentNode,
  currentSection: ContentNode,
): EditSectionButtonViewModel {
  const service = useContentEditorService();
  const entityConverter = useEntityConverter();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [title, setTitle] = useState<TiptapOutputType>(currentSection.title ?? '');
  const [shortTitle, setShortTitle] = useState<TiptapOutputType>(currentSection.shortTitle ?? '');
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isShortTitleValid, setIsShortTitleValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined);

  // When the user clicks the pencil, seed the inputs with the current section values.
  // Assume the existing title is valid (it was accepted at creation time).
  const openModal = useCallback(() => {
    setTitle(currentSection.title ?? '');
    setShortTitle(currentSection.shortTitle ?? '');
    setIsTitleValid(true);
    setIsShortTitleValid(true);
    setSaveErrorMessage(undefined);
    setIsModalVisible(true);
  }, [currentSection]);

  // When the modal is dismissed, reset all form state.
  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setTitle('');
    setShortTitle('');
    setIsTitleValid(false);
    setIsShortTitleValid(true);
    setIsSaving(false);
    setSaveErrorMessage(undefined);
  }, []);

  // When the title input changes, store the new value and let the input
  // tell us whether it passes validation. Length bounds are enforced by
  // the input itself, not the view model.
  const updateTitle = useCallback((newTitle: TiptapOutputType, isValidationPassed: boolean) => {
    setTitle(newTitle);
    setIsTitleValid(isValidationPassed);
  }, []);

  // When the short title input changes, store the new value and its validity.
  // The short title is optional, so an empty value is valid.
  const updateShortTitle = useCallback(
    (newShortTitle: TiptapOutputType, isValidationPassed: boolean) => {
      setShortTitle(newShortTitle);
      setIsShortTitleValid(isValidationPassed);
    },
    [],
  );

  // When the user confirms, save the new title/short title and close the modal.
  const confirmSave = useCallback(() => {
    if (!isTitleValid || !isShortTitleValid || isSaving) return;
    const content = service.content;
    if (!content) return;
    setIsSaving(true);
    setSaveErrorMessage(undefined);
    void (async () => {
      try {
        // Persist a blank short title as undefined so it clears the field, matching legacy behavior.
        const shortTitleToSave =
          entityConverter(shortTitle).trim().length > 0 ? shortTitle : undefined;
        const updated = renameSectionInPage(
          content,
          currentPage.id,
          currentSection.id,
          title,
          shortTitleToSave,
        );
        if (!updated) return;
        service.stageEdit(updated);
        await service.flush();
        dismissModal();
      } catch (error) {
        // Only handle the save-failure case inline; rethrowing anything else
        // turns it into an unhandled rejection so RUM's global
        // `unhandledrejection` listener reports it.
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save edited section title to working copy');
        setSaveErrorMessage(
          'There was an error saving the title. Please wait a few seconds and retry.',
        );
      } finally {
        setIsSaving(false);
      }
    })();
  }, [
    isTitleValid,
    isShortTitleValid,
    isSaving,
    service,
    title,
    shortTitle,
    entityConverter,
    currentPage,
    currentSection,
    dismissModal,
  ]);

  return {
    isModalVisible,
    title,
    shortTitle,
    isSaveEnabled: isTitleValid && isShortTitleValid && !isSaving,
    isSaving,
    saveErrorMessage,
    openModal,
    dismissModal,
    confirmSave,
    updateTitle,
    updateShortTitle,
  };
}
